import { Check, Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { useAccounts } from "@/hooks/useAccounts";
import { cn } from "@/lib/cn";
import type { ConnectionTestResult } from "@/types/account";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/Icon";
import { Input } from "@/components/ui/input";
import { MaskableInput } from "@/components/ui/maskable-input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// ============ Input Field with Icon ============
interface ApiInputFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  hint: string;
  autoComplete?: string;
  name?: string;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>["inputMode"];
  autoCapitalize?: React.InputHTMLAttributes<HTMLInputElement>["autoCapitalize"];
  autoCorrect?: React.InputHTMLAttributes<HTMLInputElement>["autoCorrect"];
  spellCheck?: boolean;
  maskable?: boolean;
  maskByDefault?: boolean;
  className?: string;
}

const ApiInputField = ({
  value,
  onChange,
  placeholder,
  hint,
  autoComplete,
  name,
  inputMode,
  autoCapitalize,
  autoCorrect,
  spellCheck,
  maskable = false,
  maskByDefault = true,
  className,
}: ApiInputFieldProps) => {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="relative">
        {/* Key icon */}
        <div className="pointer-events-none absolute top-1/2 left-4 z-10 -translate-y-1/2">
          <Icon
            name="diamond"
            className="size-6 text-neutral-400"
          />
        </div>

        {maskable ? (
          <MaskableInput
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            name={name}
            inputMode={inputMode}
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            spellCheck={spellCheck}
            maskByDefault={maskByDefault}
            className="pl-12"
          />
        ) : (
          value && (
            <div className="absolute top-1/2 right-4 -translate-y-1/2">
              <Check className="text-primary-400 size-6" />
            </div>
          )
        )}
      </div>

      {/* Hint text */}
      <p className="fs-200 text-neutral-200/50">{hint}</p>
    </div>
  );
};

// ============ Connection Test Result ============
interface ConnectionTestResultViewProps {
  result: ConnectionTestResult;
  error?: string | null;
}

const ConnectionTestResultView = ({ result, error }: ConnectionTestResultViewProps) => {
  if (error) {
    return (
      <div className="bg-danger/10 flex flex-col gap-3 rounded-sm p-4">
        <div className="text-danger flex items-center gap-2">
          <Icon
            name="cancel-circle"
            className="size-5"
          />
          <span className="fs-300 font-semibold">{error}</span>
        </div>

        {result.status === "error" && (result.error_code || result.error_details) && (
          <details className="text-danger/80">
            <summary className="fs-200 cursor-pointer">Технические детали</summary>
            <div className="bg-danger/5 mt-2 flex flex-col gap-1 rounded p-3">
              {result.error_code && (
                <div className="fs-200">
                  <span className="font-semibold">Код ошибки:</span> {result.error_code}
                </div>
              )}
              {result.error_details && (
                <div className="fs-200">
                  <span className="font-semibold">Детали:</span> {result.error_details}
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    );
  }

  if (result.status === "can_restore") {
    return (
      <div className="bg-warning/10 flex flex-col gap-2 rounded-sm p-4">
        <div className="text-warning flex items-center gap-2">
          <Icon
            name="alert-triangle"
            className="size-5"
          />
          <span className="fs-300 font-semibold">{result.message}</span>
        </div>
        <p className="fs-200 text-neutral-300">
          Создание нового аккаунта восстановит ваш удаленный аккаунт с тем же API-ключом.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-success/10 flex flex-col gap-3 rounded-sm p-4">
      <div className="text-success flex items-center gap-2">
        <Icon
          name="check-circle"
          className="size-5"
        />
        <span className="fs-300 font-semibold">Соединение успешно!</span>
      </div>

      {result.account_info && (
        <div className="flex flex-col gap-2">
          <span className="fs-200 font-semibold text-neutral-200">Информация об аккаунте:</span>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {result.account_info.uid && (
              <>
                <span className="fs-200 text-neutral-400">UID:</span>
                <span className="fs-200 text-neutral-200">{result.account_info.uid}</span>
              </>
            )}
            {result.account_info.main_uid && (
              <>
                <span className="fs-200 text-neutral-400">Main UID:</span>
                <span className="fs-200 text-neutral-200">{result.account_info.main_uid}</span>
              </>
            )}
            {result.account_info.account_level && (
              <>
                <span className="fs-200 text-neutral-400">Уровень аккаунта:</span>
                <span className="fs-200 text-neutral-200">{result.account_info.account_level}</span>
              </>
            )}
            {result.account_info.account_mode && (
              <>
                <span className="fs-200 text-neutral-400">Режим аккаунта:</span>
                <span className="fs-200 text-neutral-200">{result.account_info.account_mode}</span>
              </>
            )}
            {result.account_info.position_mode && (
              <>
                <span className="fs-200 text-neutral-400">Режим позиций:</span>
                <span className="fs-200 text-neutral-200">{result.account_info.position_mode}</span>
              </>
            )}
            {result.account_info.kyc_level && (
              <>
                <span className="fs-200 text-neutral-400">KYC уровень:</span>
                <span className="fs-200 text-neutral-200">{result.account_info.kyc_level}</span>
              </>
            )}
            {result.account_info.permissions && (
              <>
                <span className="fs-200 text-neutral-400">Разрешения:</span>
                <span className="fs-200 text-neutral-200">
                  {result.account_info.permissions.join(", ")}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Types ============
interface FormData {
  name: string;
  apiKey: string;
  apiSecret: string;
  passphrase: string;
  demo: boolean;
}

// ============ Main Dialog ============
interface AddApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: FormData) => Promise<void>;
  editingKey?: { id: string; name: string };
  isSaving?: boolean;
}

export const AddApiKeyDialog = ({
  open,
  onOpenChange,
  onSave,
  editingKey,
  isSaving,
}: AddApiKeyDialogProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const { testConnectionWithCredentials, isTestingWithCredentials } = useAccounts();

  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [demo, setDemo] = useState(false);

  // Connection test state
  const [connectionTestPassed, setConnectionTestPassed] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<ConnectionTestResult | null>(
    null
  );
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    apiKey?: string;
    apiSecret?: string;
    passphrase?: string;
  }>({});

  useEffect(() => {
    if (!open) return;

    // для редактирования подставляем только имя, ключи заново вводятся
    const resetTimer = window.setTimeout(() => {
      setName(editingKey?.name ?? "");
      setApiKey("");
      setApiSecret("");
      setPassphrase("");
      setDemo(false);

      setConnectionTestPassed(false);
      setConnectionTestResult(null);
      setConnectionError(null);
      setErrors({});
    }, 0);

    return () => window.clearTimeout(resetTimer);
  }, [open, editingKey?.name]);

  // Reset connection test when credentials change
  const handleCredentialsChange = () => {
    setConnectionTestPassed(false);
    setConnectionTestResult(null);
    setConnectionError(null);
  };

  const validateForTest = (): boolean => {
    const newErrors: typeof errors = {};

    if (!apiKey.trim()) {
      newErrors.apiKey = "API-ключ обязателен";
    }
    if (!apiSecret.trim()) {
      newErrors.apiSecret = "Секретный ключ обязателен";
    }
    if (!passphrase.trim()) {
      newErrors.passphrase = "Пасс-фраза обязательна для OKX";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForTest()) return;

    setConnectionError(null);
    setConnectionTestPassed(false);
    setConnectionTestResult(null);

    try {
      const result = await testConnectionWithCredentials({
        name: "", // Name is set on the backend
        api_key: apiKey.trim(),
        api_secret: apiSecret.trim(),
        passphrase: passphrase.trim(),
        demo,
      });

      if (result.status === "success" || result.status === "can_restore") {
        setConnectionTestPassed(true);
        setConnectionTestResult(result);
        setConnectionError(null);
      } else {
        setConnectionTestResult(result);
        setConnectionError(result.message || "Ошибка при проверке соединения");
      }
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : "Ошибка при проверке соединения");
      setConnectionTestResult(null);
    }
  };

  const isValidForTest = apiKey.length > 0 && apiSecret.length > 0 && passphrase.length > 0;
  const isValidForSave = isValidForTest && name.trim().length > 0;
  const canSave = isValidForSave && (editingKey || connectionTestPassed) && !isSaving && !isTestingWithCredentials;

  const handleSave = async () => {
    if (!canSave) return;

    if (!editingKey && !connectionTestPassed) {
      setConnectionError("Необходимо проверить соединение перед сохранением аккаунта");
      return;
    }

    setConnectionError(null);

    try {
      await onSave({ name: name.trim(), apiKey, apiSecret, passphrase, demo });

      // чистим и закрываем только после успеха
      setName("");
      setApiKey("");
      setApiSecret("");
      setPassphrase("");
      setDemo(false);
      onOpenChange(false);
    } catch (e) {
      // оставляем форму открытой и данные на месте
      setConnectionError(e instanceof Error ? e.message : "Ошибка при сохранении аккаунта");
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await handleSave();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        ref={contentRef}
        tabIndex={-1}
        aria-describedby={undefined}
        side="right"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus();
        }}
        className={cn(
          "gap-12 border-none bg-neutral-900 p-4",
          "w-full sm:w-145 sm:max-w-145 sm:p-10",
          "flex flex-col justify-center overflow-y-auto",
          // Hide default close button
          "**:data-[slot=sheet-close]:hidden"
        )}
      >
        <form
          className="flex w-full max-w-96 flex-col gap-12 self-center sm:max-w-none"
          onSubmit={handleSubmit}
        >
          {/* Header */}
          <SheetHeader className="gap-3.5 p-0 text-left">
            <SheetTitle className="text-neutral-50 fs-600 font-bold">
              {editingKey ? `Изменение API-ключа «${editingKey.name}»` : "Добавление API-ключа"}
            </SheetTitle>
          </SheetHeader>

          {/* Account name field */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <div className="pointer-events-none absolute top-1/2 left-4 z-10 -translate-y-1/2">
                <Icon
                  name="user"
                  className="size-6 text-neutral-400"
                />
              </div>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder="Название аккаунта"
                name="accountName"
                autoComplete="off"
                className={cn("pl-12", errors.name && "border-danger")}
              />
            </div>
            {errors.name ? (
              <p className="fs-200 text-danger">{errors.name}</p>
            ) : (
              <p className="fs-200 text-neutral-200/50">
                Введите название для идентификации аккаунта
              </p>
            )}
          </div>

          {/* API credentials fields */}
          <div className="flex flex-col gap-3">
            <ApiInputField
              value={apiKey}
              onChange={(value) => {
                setApiKey(value);
                handleCredentialsChange();
                if (errors.apiKey) setErrors((prev) => ({ ...prev, apiKey: undefined }));
              }}
              placeholder="API-ключ"
              hint={errors.apiKey || "Введите Ваш API-ключ с биржи"}
              name="apiKey"
              autoComplete="off"
              inputMode="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maskable
              maskByDefault
            />

            <ApiInputField
              value={apiSecret}
              onChange={(value) => {
                setApiSecret(value);
                handleCredentialsChange();
                if (errors.apiSecret) setErrors((prev) => ({ ...prev, apiSecret: undefined }));
              }}
              placeholder="Секретный ключ (API-secret)"
              hint={errors.apiSecret || "Ваш секретный API-ключ, генерируется на бирже"}
              name="apiSecret"
              autoComplete="off"
              inputMode="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maskable
              maskByDefault
            />
            <ApiInputField
              value={passphrase}
              onChange={(value) => {
                setPassphrase(value);
                handleCredentialsChange();
                if (errors.passphrase) setErrors((prev) => ({ ...prev, passphrase: undefined }));
              }}
              placeholder="Пасс-фраза (passphrase)"
              hint={errors.passphrase || "Ваша пасс-фраза, задается на бирже"}
              name="passphrase"
              autoComplete="off"
              inputMode="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maskable
              maskByDefault
            />

            {/* Demo mode checkbox */}
            <label className="flex cursor-pointer items-center gap-3">
              <Checkbox
                checked={demo}
                onCheckedChange={(checked) => {
                  setDemo(checked === true);
                  handleCredentialsChange();
                }}
              />
              <span className="fs-300 text-neutral-200">Демо-режим</span>
            </label>
          </div>

          {/* Test connection section */}
          {!editingKey && (
            <div className="flex flex-col gap-4">
              <Button
                type="button"
                variant="neutral"
                size="md"
                className="w-full"
                onClick={handleTestConnection}
                disabled={isTestingWithCredentials || !isValidForTest}
              >
                {isTestingWithCredentials ? (
                  <>
                    <Loader2 className="size-5 animate-spin" />
                    Проверяем соединение...
                  </>
                ) : (
                  <>
                    <Icon
                      name="refresh"
                      className="size-5"
                    />
                    Проверить соединение
                  </>
                )}
              </Button>

              {/* Connection test result */}
              {(connectionTestResult || connectionError) && (
                <ConnectionTestResultView
                  result={
                    connectionTestResult || { status: "error", message: connectionError || "" }
                  }
                  error={connectionError}
                />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full"
              disabled={!canSave}
              icon={
                isSaving ? (
                  <Loader2 className="size-6 animate-spin" />
                ) : (
                  <Icon
                    name="check-circle"
                    className="size-6"
                  />
                )
              }
            >
              {isSaving ? "Сохранение..." : editingKey ? "Сохранить" : "Создать аккаунт"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={handleCancel}
              icon={
                <Icon
                  name="cancel-circle"
                  className="size-6"
                />
              }
            >
              Отмена
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
